import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Loader2, CheckCircle, KeyRound, User, Phone, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialMode?: 'signin' | 'signup';
}

type AuthMode = 'signin' | 'signup';

export function AuthModal({ open, onOpenChange, initialMode }: AuthModalProps) {
  const [mode, setMode] = useState<AuthMode>(initialMode || 'signin');

  // Sync mode when initialMode or open changes
  useEffect(() => {
    if (open && initialMode) {
      setMode(initialMode);
    }
  }, [open, initialMode]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [forgotPassword, setForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const { signInWithMagicLink, signUpWithPassword, signInWithPassword, resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    if (mode === 'signup' && !acceptedTerms) {
      setError('Você precisa aceitar os Termos de Uso e a Política de Privacidade.');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    if (mode === 'signup') {
      if (password.trim()) {
        // Signup with password
        const { error } = await signUpWithPassword(email, password, {
          displayName: displayName.trim() || undefined,
          phone: phone.trim() || undefined,
        });
        if (error) {
          setError(error.message === 'User already registered' 
            ? 'Este email já está cadastrado. Tente entrar.'
            : 'Erro ao criar conta. Tente novamente.');
        } else {
          setSuccess('Conta criada! Verifique seu email para confirmar o cadastro.');
        }
      } else {
        // Signup via magic link (no password)
        const { error } = await signInWithMagicLink(email);
        if (error) {
          setError('Erro ao enviar o link. Verifique o email e tente novamente.');
        } else {
          setEmailSent(true);
        }
      }
    } else {
      // Sign in
      if (password.trim()) {
        const { error } = await signInWithPassword(email, password);
        if (error) {
          setError('Email ou senha incorretos. Tente novamente.');
        } else {
          handleClose();
        }
      } else {
        const { error } = await signInWithMagicLink(email);
        if (error) {
          setError('Erro ao enviar o link. Verifique o email e tente novamente.');
        } else {
          setEmailSent(true);
        }
      }
    }

    setIsLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsLoading(true);
    setError('');
    const { error } = await resetPassword(email);
    if (error) {
      setError('Erro ao enviar email de recuperação. Tente novamente.');
    } else {
      setResetEmailSent(true);
    }
    setIsLoading(false);
  };

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setDisplayName('');
    setPhone('');
    setAcceptedTerms(false);
    setEmailSent(false);
    setResetEmailSent(false);
    setForgotPassword(false);
    setError('');
    setSuccess('');
    onOpenChange(false);
  };

  const switchMode = () => {
    setMode(mode === 'signin' ? 'signup' : 'signin');
    setForgotPassword(false);
    setResetEmailSent(false);
    setError('');
    setSuccess('');
    setPassword('');
    setDisplayName('');
    setPhone('');
    setAcceptedTerms(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md glass-card border-primary/20">
        <DialogHeader className="text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center mb-4">
            {emailSent || success ? (
              <CheckCircle className="w-8 h-8 text-white" />
            ) : (
              <KeyRound className="w-8 h-8 text-white" />
            )}
          </div>
          <DialogTitle className="font-display text-2xl">
            {resetEmailSent ? 'Verifique seu email!' : emailSent ? 'Verifique seu email!' : success ? 'Conta criada!' : forgotPassword ? 'Recuperar senha' : mode === 'signin' ? 'Entrar' : 'Criar conta'}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {resetEmailSent
              ? 'Enviamos um link para redefinir sua senha.'
              : emailSent
              ? 'Enviamos um link de acesso para o seu email. Basta clicar nele para entrar.'
              : success
              ? success
              : forgotPassword
              ? 'Digite seu email e enviaremos um link para redefinir sua senha.'
              : mode === 'signin'
              ? 'Digite seu email para entrar. A senha é opcional — sem ela, enviaremos um link de acesso.'
              : 'Crie sua conta. Apenas o email é obrigatório — os demais campos são opcionais.'
            }
          </DialogDescription>
        </DialogHeader>

        {resetEmailSent ? (
          <div className="space-y-4 mt-4">
            <div className="p-4 rounded-xl bg-primary/10 border border-primary/20 space-y-2">
              <p className="text-sm text-center font-medium">📬 Email enviado para:</p>
              <p className="text-sm text-center text-primary font-semibold break-all">{email}</p>
              <p className="text-xs text-center text-muted-foreground">Clique no link do email para redefinir sua senha.</p>
            </div>
            <Button variant="outline" className="w-full" onClick={handleClose}>
              Fechar
            </Button>
          </div>
        ) : forgotPassword ? (
          <form onSubmit={handleForgotPassword} className="space-y-4 mt-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs text-muted-foreground">Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 bg-background/50 border-primary/20 focus:border-primary"
                  required
                />
              </div>
            </div>
            {error && <p className="text-sm text-destructive text-center">{error}</p>}
            <Button type="submit" className="w-full h-12 font-semibold" disabled={isLoading}>
              {isLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando...</> : <>Enviar link de recuperação</>}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              <button type="button" onClick={() => { setForgotPassword(false); setError(''); }} className="text-primary hover:underline font-medium">Voltar ao login</button>
            </p>
          </form>
        ) : !emailSent && !success ? (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs text-muted-foreground">Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 bg-background/50 border-primary/20 focus:border-primary"
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs text-muted-foreground">
                Senha {mode === 'signup' ? '(opcional)' : '(opcional — deixe vazio para receber link)'}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-11 bg-background/50 border-primary/20 focus:border-primary"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {mode === 'signup' && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="displayName" className="text-xs text-muted-foreground">Nome (opcional)</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="displayName"
                      type="text"
                      placeholder="Seu nome"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="pl-10 h-11 bg-background/50 border-primary/20 focus:border-primary"
                      maxLength={100}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="phone" className="text-xs text-muted-foreground">Telefone (opcional)</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(11) 99999-9999"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="pl-10 h-11 bg-background/50 border-primary/20 focus:border-primary"
                      maxLength={20}
                    />
                  </div>
                </div>
              </>
            )}

            {mode === 'signup' && (
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-0.5 rounded border-primary/30 text-primary focus:ring-primary"
                />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  Li e concordo com os{' '}
                  <Link to="/termos-de-uso" target="_blank" className="text-primary hover:underline font-medium">Termos de Uso</Link>
                  {' '}e{' '}
                  <Link to="/politica-de-privacidade" target="_blank" className="text-primary hover:underline font-medium">Política de Privacidade</Link>.
                </span>
              </label>
            )}


            {error && (
              <p className="text-sm text-destructive text-center">{error}</p>
            )}

            <Button
              type="submit"
              className="w-full h-12 font-semibold"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {password.trim() ? 'Processando...' : 'Enviando...'}
                </>
              ) : password.trim() ? (
                mode === 'signin' ? 'Entrar com senha' : 'Criar conta'
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Enviar link de acesso
                </>
              )}
            </Button>

            {mode === 'signin' && (
              <p className="text-center">
                <button type="button" onClick={() => { setForgotPassword(true); setError(''); }} className="text-xs text-muted-foreground hover:text-primary hover:underline">
                  Esqueci minha senha
                </button>
              </p>
            )}

            <p className="text-center text-sm text-muted-foreground">
              {mode === 'signin' ? (
                <>Não tem conta?{' '}<button type="button" onClick={switchMode} className="text-primary hover:underline font-medium">Criar conta</button></>
              ) : (
                <>Já tem conta?{' '}<button type="button" onClick={switchMode} className="text-primary hover:underline font-medium">Entrar</button></>
              )}
            </p>
          </form>
        ) : emailSent ? (
          <div className="space-y-4 mt-4">
            <div className="p-4 rounded-xl bg-primary/10 border border-primary/20 space-y-2">
              <p className="text-sm text-center font-medium">📬 Email enviado para:</p>
              <p className="text-sm text-center text-primary font-semibold break-all">{email}</p>
              <p className="text-xs text-center text-muted-foreground">Não encontrou? Verifique a pasta de spam.</p>
            </div>
            <Button variant="outline" className="w-full" onClick={() => setEmailSent(false)}>
              Usar outro email
            </Button>
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            <div className="p-4 rounded-xl bg-primary/10 border border-primary/20 space-y-2">
              <p className="text-sm text-center font-medium">✅ {success}</p>
            </div>
            <Button variant="outline" className="w-full" onClick={handleClose}>
              Fechar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
